// Google Pay as a payout method: the recipient picks a card in the Google Pay sheet and we
// push the money to it, exactly like "Direct to Debit Card" but without typing the number.
//
// Google encrypts the card to the CDE's public key (DIRECT tokenization, ECv2), so this page
// only ever holds an opaque token; it posts that straight to the CDE, which is the only thing
// that can open it. We ask for PAN_ONLY: that yields the card number saved in the Google
// account, which NYCE can push to. Device tokens (CRYPTOGRAM_3DS) can't receive a push today.
import axios from "axios";

const SCRIPT_SRC = "https://pay.google.com/gp/p/js/pay.js";

const config = {
  environment: process.env.REACT_APP_GOOGLE_PAY_ENVIRONMENT || "TEST",
  merchantId: process.env.REACT_APP_GOOGLE_PAY_MERCHANT_ID,
  // Base64 uncompressed P-256 point; the private half lives in the CDE.
  publicKey: process.env.REACT_APP_GOOGLE_PAY_PUBLIC_KEY,
  cdeApiUrl: process.env.REACT_APP_P2C_API_URL,
};

// Off unless this build was given everything it needs.
export const googlePayConfigured = () =>
  Boolean(config.merchantId && config.publicKey && config.cdeApiUrl);

const cardMethod = {
  type: "CARD",
  parameters: {
    allowedAuthMethods: ["PAN_ONLY"],
    allowedCardNetworks: ["VISA", "MASTERCARD", "DISCOVER"],
    // Push-to-card is debit only; the CDE's BIN check still has the final say.
    allowCreditCards: false,
    // The name on the card isn't in the encrypted payload; the billing address carries it.
    billingAddressRequired: true,
    billingAddressParameters: { format: "MIN" },
  },
};

const tokenizedCardMethod = {
  ...cardMethod,
  tokenizationSpecification: {
    type: "DIRECT",
    parameters: { protocolVersion: "ECv2", publicKey: config.publicKey },
  },
};

const baseRequest = { apiVersion: 2, apiVersionMinor: 0 };

let clientPromise = null;

// Loads pay.js once and resolves to a PaymentsClient.
const getClient = () => {
  if (!clientPromise) {
    clientPromise = new Promise((resolve, reject) => {
      const done = () =>
        resolve(
          new window.google.payments.api.PaymentsClient({
            environment: config.environment,
          })
        );
      if (window.google?.payments?.api) {
        done();
        return;
      }
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = done;
      script.onerror = () => {
        clientPromise = null;
        reject(new Error("Google Pay failed to load"));
      };
      document.body.appendChild(script);
    });
  }
  return clientPromise;
};

// True when this browser can show the Google Pay sheet. Never throws.
export const googlePayAvailable = async () => {
  if (!googlePayConfigured()) return false;
  try {
    const client = await getClient();
    const { result } = await client.isReadyToPay({
      ...baseRequest,
      allowedPaymentMethods: [cardMethod],
    });
    return Boolean(result);
  } catch (err) {
    console.warn("Google Pay unavailable", err);
    return false;
  }
};

// Opens the sheet. Must run from a click: googlePayAvailable() has already loaded pay.js,
// so the call below happens synchronously inside the user gesture. Resolves to
// { token, cardholderName }, or null if the recipient closed the sheet.
export const pickGooglePayCard = async (merchantName) => {
  const client = await getClient();
  try {
    const paymentData = await client.loadPaymentData({
      ...baseRequest,
      allowedPaymentMethods: [tokenizedCardMethod],
      merchantInfo: { merchantId: config.merchantId, merchantName },
      // We're paying the recipient, not charging them: show no total.
      transactionInfo: {
        totalPriceStatus: "NOT_CURRENTLY_KNOWN",
        currencyCode: "USD",
        countryCode: "US",
      },
    });
    const method = paymentData.paymentMethodData;
    return {
      token: method.tokenizationData.token,
      cardholderName: method.info?.billingAddress?.name || "",
    };
  } catch (err) {
    if (err?.statusCode === "CANCELED") return null;
    throw err;
  }
};

// Hands the encrypted token to the CDE, which decrypts it and starts the push.
export const submitGooglePayCard = (
  transferId,
  inviteId,
  { token, cardholderName },
  visaDirectEnabled
) =>
  axios.post(
    `${config.cdeApiUrl}/google_pay/push_to_card/${transferId}`,
    {
      invitation_id: inviteId,
      token,
      cardholder_name: cardholderName,
      visa_direct_enabled: visaDirectEnabled,
    },
    { headers: { "Content-Type": "application/json" } }
  );
